import React, { MouseEventHandler } from 'react';
import _, { get, isEmpty, uniqueId } from 'lodash';
import { injectIntl, IntlShape } from 'react-intl';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';
import moment from 'moment-timezone';
import { CheckCircle, RadioButtonUnchecked } from '@material-ui/icons';
import { getVehicleFromSagisInfo } from 'containers/PreOpeningPage/OpposingParty/utils';
import { StyledProcedureInformationRecap } from 'containers/common/LegalProceedings/JudgementAdd/Recap/styled-component';
import { Listings } from 'containers/AppContainer/typings';
import { translate } from 'containers/services/translate';
import { Idetails } from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/typings';
import {
  getValueByCode,
  getVehicules,
} from 'containers/common/LegalProceedings/JudgementAdd/Recap/utils';
import { DETAIL_JUGEMENT_ADD, LISTINGS } from 'containers/data-structure';
import { PROCEDURE_NATURES } from 'containers/LawyerLegalProceedingsPage/constants';
import messages from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/messages';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { SuedPartiesContainer } from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/styled-components';
import { IsSuedPartyChecked } from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/utils';
import { isDriverSelected } from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/utils';
import { isMonitoringCourtManager } from 'helpers/utils';
import currentUserStorage from 'containers/services/security/current-user-storage';

export interface ProcedureInformationRecapProps {
  intl: IntlShape;
  listings: Listings;
  details: Idetails;
  readOnly?: boolean;
  stakeholders?: any;
  assignedLawyer?: any;
  handleOnEditClick?: MouseEventHandler;
  creator?: string;
  sagisOpposingParties: any;
}

export const ProcedureInformationRecap: React.FC<ProcedureInformationRecapProps> = (
  props,
): JSX.Element => {
  return (
    <StyledProcedureInformationRecap>
      <section className="procedure-information-title">
        <div>{translate(messages.procedureInformationTitle, props)}</div>
        {!props.readOnly &&
          (!isMonitoringCourtManager() ||
            props.creator === currentUserStorage.get().username) && (
            <SecondaryButton
              title={translate(messages.edit, props)}
              onClick={props.handleOnEditClick}
            />
          )}
      </section>
      <section className="procedure">
        <article className="field">
          <div className="label">
            {translate(messages.procedureNature, props)}
          </div>
          <div className="value">
            {getValueByCode(
              get(props.details, DETAIL_JUGEMENT_ADD.PROCEDURE_NATURE_ID),
              get(props.listings, LISTINGS.PROCEDURE_NATURE, []),
            )}
          </div>
        </article>
        <article className="field">
          <div className="label">{translate(messages.jurisdiction, props)}</div>
          <div className="value">
            {getValueByCode(
              get(props.details, DETAIL_JUGEMENT_ADD.JURISDICTION_ID),
              get(props.listings, LISTINGS.JURISDICTION, []),
            )}
          </div>
        </article>
        {!_.isEmpty(get(props.details, DETAIL_JUGEMENT_ADD.HEARING_DATE)) && (
          <article className="field">
            <div className="label">
              {translate(messages.hearingDateTitle, props)}
            </div>
            <div className="value">
              {moment(
                get(props.details, DETAIL_JUGEMENT_ADD.HEARING_DATE),
              ).format('YYYY-MM-DD')}
            </div>
          </article>
        )}
        <article className="field">
          <div className="label">{translate(messages.courtFile, props)}</div>
          <div className="value">
            {get(props.details, DETAIL_JUGEMENT_ADD.COURT_FILE)}
          </div>
        </article>
      </section>
      {String(get(props.details, DETAIL_JUGEMENT_ADD.PROCEDURE_NATURE_ID)) ===
        PROCEDURE_NATURES.CORRECTIONAL &&
        !featureService.hasFeature(FEATURES.SUED_PARTY) && (
          <section className="correctional-detail">
            <article className="field">
              <div className="label">
                {translate(messages.packageContinuation, props)}
              </div>
              <div className="value">
                {get(props.details, DETAIL_JUGEMENT_ADD.PACKAGE_CONTINUATION)}
              </div>
            </article>
            <article className="checkbox">
              <div className="checkbox-label">
                {translate(messages.isInsuredSued, props)}
              </div>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={true}
                    checkedIcon={<CheckCircle />}
                    icon={<RadioButtonUnchecked />}
                    disabled
                  />
                }
                label={
                  get(props.details, DETAIL_JUGEMENT_ADD.IS_INSURED_SUED)
                    ? translate(messages.yes, props)
                    : translate(messages.no, props)
                }
              />
            </article>
          </section>
        )}
      {String(get(props.details, DETAIL_JUGEMENT_ADD.PROCEDURE_NATURE_ID)) ===
        PROCEDURE_NATURES.CORRECTIONAL &&
        featureService.hasFeature(FEATURES.SUED_PARTY) && (
          <section className="correctional-detail">
            <article className="field">
              <div className="label">
                {translate(messages.packageContinuation, props)}
              </div>
              <div className="value">
                {get(props.details, DETAIL_JUGEMENT_ADD.PACKAGE_CONTINUATION)}
              </div>
            </article>
            <FormGroup>
              <div className="sued-party-title">
                <span className="checkbox-form-label">
                  {translate(messages.suedParties, props)}
                </span>
              </div>
              {props.details[DETAIL_JUGEMENT_ADD.SUED_PARTIES]?.map(
                (el: any, index) => (
                  <FormControlLabel
                    key={index}
                    control={
                      <Checkbox
                        data-testid="checkboxId"
                        checked={IsSuedPartyChecked(
                          el.suedParty,
                          props.details[DETAIL_JUGEMENT_ADD.SUED_PARTIES],
                        )}
                        color="primary"
                        disabled
                      />
                    }
                    label={el.title}
                  />
                ),
              )}
            </FormGroup>
            <SuedPartiesContainer>
              <div className="sued_party_container">
                {isDriverSelected(props.details) &&
                  getVehicules(props.details)?.map(vehicule => (
                    <FormGroup key={uniqueId()} className="vehicule_div">
                      <FormControlLabel
                        control={
                          <Checkbox checked={true} color="primary" disabled />
                        }
                        label={
                          featureService.hasFeature(
                            FEATURES.MIGRATION_CLAIM_INFO,
                          ) &&
                          !isEmpty(
                            getVehicleFromSagisInfo(
                              get(vehicule, 'partKey'),
                              props.sagisOpposingParties,
                            ),
                          )
                            ? get(
                                getVehicleFromSagisInfo(
                                  get(vehicule, 'partKey'),
                                  props.sagisOpposingParties,
                                ),
                                'lastName',
                              ) +
                              ' ' +
                              get(
                                getVehicleFromSagisInfo(
                                  get(vehicule, 'partKey'),
                                  props.sagisOpposingParties,
                                ),
                                'firstName',
                              )
                            : vehicule.lastName + ' ' + vehicule.firstName
                        }
                      />
                    </FormGroup>
                  ))}
              </div>
            </SuedPartiesContainer>
          </section>
        )}

      {props.assignedLawyer && props.stakeholders && (
        <FormControl className="input">
          <InputLabel id="idd-label-id">
            {translate(messages.assignedLawyerTitle, props)}
          </InputLabel>
          <Select disabled labelId="idd-label-id" value={props.assignedLawyer}>
            {props.stakeholders &&
              props.stakeholders.map(
                /* istanbul ignore next */
                (element: { ItemKey: number; ItemDescription: string }) => (
                  /* istanbul ignore next */ <MenuItem value={element.ItemKey}>
                    {element.ItemDescription}
                  </MenuItem>
                ),
              )}
          </Select>
        </FormControl>
      )}
    </StyledProcedureInformationRecap>
  );
};

ProcedureInformationRecap.defaultProps = {
  readOnly: false,
};

export default injectIntl(ProcedureInformationRecap);
